import React, { useEffect, useState } from 'react';
import { doctoralApi } from '@/features/workspace/api';
import { ARCHIVE_DOCS, OFFICIAL } from '../doctoralTemplate';
import { DoctoralRecordData } from '../types';
import { Hero } from './dp120Parts';

type CompletionTab = 'PROGRAM' | 'EMPLOYMENT' | 'ARCHIVE' | 'DIPLOMA';

export const DoctoralCompletionView: React.FC = () => {
  const [activeTabKey, setActiveTabKey] = useState<CompletionTab>('PROGRAM');
  const [record, setRecord] = useState<DoctoralRecordData | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [employmentForm, setEmploymentForm] = useState({
    employment_status: 'EMPLOYED',
    employer: '',
    position: '',
    city_country: '',
    employment_start_date: '',
    work_obligation: '',
  });
  const [diplomaForm, setDiplomaForm] = useState({
    diploma_number: '',
    diploma_issue_date: '',
    diploma_verification_status: 'UPLOADED',
  });
  const [archiveForm, setArchiveForm] = useState({
    archive_status: 'COLLECTING',
    archive_completed_at: '',
    archive_files_count: 0,
  });

  useEffect(() => {
    if (typeof doctoralApi?.record !== 'function') return;
    doctoralApi
      .record()
      .then((res) => {
        setRecord(res);
        if (!res) return;
        setEmploymentForm({
          employment_status: res.employment_status || 'EMPLOYED',
          employer: res.employer || res.workplace || '',
          position: res.position || '',
          city_country: res.city_country || '',
          employment_start_date: res.employment_start_date || '',
          work_obligation: res.work_obligation || '',
        });
        setDiplomaForm({
          diploma_number: res.diploma_number || '',
          diploma_issue_date: res.diploma_issue_date || '',
          diploma_verification_status: res.diploma_verification_status || 'UPLOADED',
        });
        setArchiveForm({
          archive_status: res.archive_status || 'COLLECTING',
          archive_completed_at: res.archive_completed_at || '',
          archive_files_count: res.archive_files_count || 0,
        });
      })
      .catch(() => setRecord(null));
  }, []);

  const saveEmployment = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    setSaveSuccess(null);
    try {
      const updated = await doctoralApi.updateEmployment(employmentForm);
      setRecord(updated);
      setSaveSuccess('Данные о трудоустройстве и отработке сохранены и переданы на кафедру');
    } catch {
      setSaveSuccess('Не удалось сохранить данные о трудоустройстве');
    } finally {
      setIsSaving(false);
    }
  };

  const saveDiploma = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    setSaveSuccess(null);
    try {
      const updated = await doctoralApi.updateDiploma(diplomaForm);
      setRecord(updated);
      setSaveSuccess('Сведения о дипломе PhD сохранены и направлены на верификацию кафедры');
    } catch {
      setSaveSuccess('Не удалось сохранить данные диплома');
    } finally {
      setIsSaving(false);
    }
  };

  const saveArchive = async (status: string) => {
    setIsSaving(true);
    setSaveSuccess(null);
    try {
      const updated = await doctoralApi.updateArchive({ ...archiveForm, archive_status: status });
      setRecord(updated);
      setArchiveForm((prev) => ({ ...prev, archive_status: status }));
      setSaveSuccess('Сведения об архивном деле обновлены');
    } catch {
      setSaveSuccess('Не удалось обновить архивное дело');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs: [CompletionTab, string][] = [
    ['PROGRAM', 'Завершение программы'],
    ['EMPLOYMENT', 'Трудоустройство и отработка'],
    ['ARCHIVE', 'Архивное дело (16 документов)'],
    ['DIPLOMA', 'Защита и диплом PhD'],
  ];

  return (
    <>
      <Hero
        kicker="AFTER STUDY / AFTER DEFENSE"
        title="Завершение обучения"
        text="Отдельный выпускной контур: завершение программы, отработка, архивное дело и данные степени PhD."
        color="#f1b93f"
      />
      {saveSuccess ? (
        <div className="dp120-callout" style={{ marginTop: 10 }}>
          <b>{saveSuccess}</b>
        </div>
      ) : null}
      <div className="dp120-guide-tabs">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={activeTabKey === key ? 'active' : ''}
            onClick={() => {
              setActiveTabKey(key);
              setSaveSuccess(null);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTabKey === 'PROGRAM' && (
        <div className="dp120-grid">
          <article className="dp120-card span6">
            <header>
              <div>
                <h3>Завершение образовательной программы</h3>
                <p>Документы после освоения 3-летней программы.</p>
              </div>
            </header>
            <div className="dp120-card-body">
              <div className="dp120-list">
                <div className="dp120-row">
                  <span>
                    <strong>Справка об освоении 180 ECTS</strong>
                    <small>Подтверждает выполнение теоретических дисциплин и НИРД</small>
                  </span>
                  <span className="dp120-badge">Подтверждение регистратором</span>
                </div>
                <div className="dp120-row">
                  <span>
                    <strong>Академический транскрипт</strong>
                    <small>Итоговые оценки за 6 семестров</small>
                  </span>
                  <span className="dp120-badge">Регистратор</span>
                </div>
                <div className="dp120-row">
                  <span>
                    <strong>Приказ об отчислении с окончанием</strong>
                    <small>Издаётся университетом по факту завершения программы</small>
                  </span>
                  <span className="dp120-badge">Приказ</span>
                </div>
              </div>
            </div>
          </article>
          <article className="dp120-card span6">
            <header>
              <div>
                <h3>Что происходит дальше</h3>
                <p>Программа может быть завершена до фактической защиты.</p>
              </div>
            </header>
            <div className="dp120-card-body">
              <div className="dp120-list">
                <div className="dp120-row">
                  <span>
                    <strong>Обходной лист и архивное дело</strong>
                    <small>Обязательный выпускной контур.</small>
                  </span>
                  <button type="button" className="dp120-mini gold" onClick={() => setActiveTabKey('ARCHIVE')}>
                    Открыть
                  </button>
                </div>
                <div className="dp120-row">
                  <span>
                    <strong>Трудоустройство и 3-летняя отработка гранта</strong>
                    <small>Для выпускников государственного заказа — отдельный процесс.</small>
                  </span>
                  <button type="button" className="dp120-mini" onClick={() => setActiveTabKey('EMPLOYMENT')}>
                    Открыть
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
      )}

      {activeTabKey === 'EMPLOYMENT' && (
        <article className="dp120-article">
          <div className="dp120-article-head">
            <span className="overline">Трудоустройство и государственный заказ</span>
            <h3>Трудоустройство и 3-летняя отработка гранта</h3>
            <p>
              Памятка ENU используется как рабочая инструкция по Platonus и документам, а правовые требования
              проверяются по актуальной редакции Правил №403.
            </p>
          </div>
          <div className="dp120-article-body">
            <section className="dp120-article-section">
              <h4>Рабочий порядок по инструкции ENU 2025</h4>
              <div className="dp120-roadmap">
                {[
                  ['01', 'Актуализировать данные', 'Проверить личные и контактные данные в АИС Platonus.'],
                  ['02', 'Подготовить ЭЦП', 'Проверить срок действия ЭЦП и работу NCALayer.'],
                  ['03', 'Заполнить все поля', 'Указать данные трудоустройства и загрузить подтверждение.'],
                  ['04', 'Проверка кафедры', 'Ответственный кафедры проверяет сведения и документы.'],
                ].map(([num, title, note]) => (
                  <div key={num} className="dp120-roadmap-step" style={{ ['--c' as string]: '#4ce2bf' }}>
                    <b>{num}</b>
                    <span>
                      <strong>{title}</strong>
                      <small>{note}</small>
                    </span>
                  </div>
                ))}
              </div>
            </section>
            <form className="dp120-form" onSubmit={saveEmployment}>
              <label className="dp120-field">
                <span>Организация</span>
                <input
                  required
                  value={employmentForm.employer}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, employer: e.target.value })}
                />
              </label>
              <label className="dp120-field">
                <span>Должность</span>
                <input
                  required
                  value={employmentForm.position}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, position: e.target.value })}
                />
              </label>
              <label className="dp120-field">
                <span>Город / страна</span>
                <input
                  value={employmentForm.city_country}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, city_country: e.target.value })}
                />
              </label>
              <label className="dp120-field">
                <span>Статус занятости</span>
                <select
                  value={employmentForm.employment_status}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, employment_status: e.target.value })}
                >
                  <option value="EMPLOYED">Трудоустроен</option>
                  <option value="VERIFIED_EMPLOYED">Верифицировано кафедрой</option>
                  <option value="LOOKING_FOR_JOB">В поиске работы</option>
                </select>
              </label>
              <label className="dp120-field full">
                <span>Обязательство по отработке гранта</span>
                <input
                  value={employmentForm.work_obligation}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, work_obligation: e.target.value })}
                />
              </label>
              <div className="dp120-course-actions">
                <button type="submit" className="dp120-btn gold" disabled={isSaving}>
                  {isSaving ? 'Сохранение...' : 'Сохранить данные о трудоустройстве'}
                </button>
              </div>
            </form>
            <div className="dp120-law-note" style={{ marginTop: 12 }}>
              <strong>Место отработки проверяется по действующей норме.</strong>
              <p>Система не помечает организацию автоматически как допустимое место отработки.</p>
              <div className="dp120-source-links">
                <a href={OFFICIAL.employmentRules} target="_blank" rel="noopener noreferrer">Актуальные Правила №403 ↗</a>
                <a href={OFFICIAL.educationLaw} target="_blank" rel="noopener noreferrer">Закон «Об образовании» ↗</a>
              </div>
            </div>
          </div>
        </article>
      )}

      {activeTabKey === 'ARCHIVE' && (
        <>
          <Hero
            kicker="FINAL ACADEMIC FILE"
            title="Архивное дело выпускника (16 документов)"
            text="Личное дело выпускника докторантуры: документы докторанта, Офиса регистратора, кафедры и технического секретаря."
            color="#f1b93f"
          />
          <div className="dp120-archive-table">
            {ARCHIVE_DOCS.map(([n, title, owner, mode, rule]) => (
              <div
                key={n}
                className={`dp120-archive-row ${mode}`}
                style={{ ['--c' as string]: mode === 'user' ? '#4ce2bf' : mode === 'department' ? '#f1b93f' : '#688798' }}
              >
                <span className="n">{String(n).padStart(2, '0')}</span>
                <span>
                  <strong>{title}</strong>
                  <small>{rule}</small>
                </span>
                <span className="dp120-archive-owner">
                  <b>Источник</b>
                  {owner}
                </span>
                <div className="dp120-archive-actions">
                  <span className="dp120-badge">{mode === 'user' ? 'Загрузка' : 'Сверка'}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="dp120-course-actions" style={{ marginTop: 12 }}>
            <button type="button" className="dp120-mini gold" disabled={isSaving} onClick={() => saveArchive('COLLECTING')}>
              Начать сбор
            </button>
            <button type="button" className="dp120-mini" disabled={isSaving} onClick={() => saveArchive('ON_REVIEW')}>
              Отправить Advisor на проверку
            </button>
            <button type="button" className="dp120-mini mint" disabled={isSaving} onClick={() => saveArchive('DONE')}>
              Сдано
            </button>
            <span className="dp120-badge">Статус: {record?.archive_status || archiveForm.archive_status}</span>
          </div>
        </>
      )}

      {activeTabKey === 'DIPLOMA' && (
        <div className="dp120-grid">
          <article className="dp120-card span8">
            <header>
              <div>
                <h3>Защита диссертации и диплом PhD</h3>
                <p>Заполняется после фактических событий.</p>
              </div>
            </header>
            <div className="dp120-card-body">
              <div className="dp120-list" style={{ marginBottom: 10 }}>
                <div className="dp120-row">
                  <span>
                    <strong>Планируемая дата защиты</strong>
                    <small>{record?.planned_defense_date || 'Не установлена'}</small>
                  </span>
                </div>
                <div className="dp120-row">
                  <span>
                    <strong>Фактическая дата защиты</strong>
                    <small>{record?.actual_defense_date || 'Ожидает решения совета'}</small>
                  </span>
                </div>
              </div>
              <form className="dp120-form" onSubmit={saveDiploma}>
                <label className="dp120-field">
                  <span>Номер / серия диплома PhD</span>
                  <input
                    required
                    value={diplomaForm.diploma_number}
                    onChange={(e) => setDiplomaForm({ ...diplomaForm, diploma_number: e.target.value })}
                  />
                </label>
                <label className="dp120-field">
                  <span>Дата выдачи диплома</span>
                  <input
                    required
                    type="date"
                    value={diplomaForm.diploma_issue_date}
                    onChange={(e) => setDiplomaForm({ ...diplomaForm, diploma_issue_date: e.target.value })}
                  />
                </label>
                <div className="dp120-course-actions">
                  <button type="submit" className="dp120-btn gold" disabled={isSaving}>
                    {isSaving ? 'Сохранение...' : 'Сохранить данные диплома'}
                  </button>
                </div>
              </form>
            </div>
          </article>
          <article className="dp120-card span4">
            <header>
              <div>
                <h3>До получения диплома</h3>
                <p>Если программа завершена, но защита ещё впереди.</p>
              </div>
            </header>
            <div className="dp120-card-body">
              <div className="dp120-callout">
                <b>Кабинет остаётся доступным.</b> После защиты запись дополняется решением и дипломом.
              </div>
            </div>
          </article>
        </div>
      )}
    </>
  );
};
